import type { Scene } from '../types'

export const investmentScenes: Scene[] = [
  {
    id: 'INV-01',
    chapter: 'investment',
    route: '/investment/implementation',
    title: 'Investment Overview',
    headline: 'A milestone-based implementation investment.',
    supporting:
      'A fixed not-to-exceed implementation price, with operational support priced separately so the District can evaluate each on its own terms.',
    visual: 'investment-cards',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Sections 9.1, 10.10' },
      { kind: 'proposal', label: 'Submitted Form A — Proposal Response' },
    ],
    presenterNotes: [
      'This section is about building the platform. State the construction figure clearly: US$610,000 not-to-exceed for implementation. The card shows the support line and the total too — say they are covered at the end, with technical support.',
      'Not-to-exceed is the important word. The District asked for total not-to-exceed costs, and that is what this is.',
      'Do not discuss internal cost structure, rates or margins under any circumstances.',
    ],
    estimatedSeconds: 45,
    rfpAlignment: ['Time and Cost Proposal — total not-to-exceed costs for implementation'],
    evalTags: ['EVAL-TC'],
    exploreDetails: [
      {
        title: 'Implementation price by work package',
        table: {
          head: ['Work package', 'Price'],
          rows: [
            ['Core Platform', 'US$ 147,000'],
            ['Land Acquisition', 'US$ 109,000'],
            ['Land Management', 'US$ 114,000'],
            ['Land Disposition', 'US$ 71,000'],
            ['GIS and Enterprise Integrations', 'US$ 93,000'],
            ['Data Migration and Document Generation', 'US$ 76,000'],
            ['Total Not-to-Exceed Implementation Price', 'US$ 610,000'],
          ],
        },
        body: [
          'Cross-cutting activities — project management, requirements refinement, architecture, UX/UI, infrastructure, quality assurance, testing, training, documentation, deployment and hypercare — are incorporated into the applicable work packages and are not charged as separate line items.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Section 9.1' }],
      },
      {
        title: 'Third-party and licensing costs',
        table: {
          head: ['Item', 'Responsibility'],
          rows: [
            ['District server infrastructure', 'District-provided and maintained, per the District\'s own answer in the Questions & Answers.'],
            ['Microsoft SQL Server', 'District-hosted environment and licensing under the District-provided infrastructure baseline.'],
            ['Microsoft Entra ID / Microsoft 365', 'Existing District Microsoft 365 Enterprise E3 environment.'],
            ['ArcGIS runtime environment', 'District-provided. The District clarified it will not supply ArcGIS developer licensing to the vendor; any developer licensing Merlion requires is procured by Merlion and absorbed within the US$610,000 price.'],
            ['Local AI inference (Gemma + Ollama)', 'No metered external LLM API fee. Locally hosted inference baseline.'],
            ['CivicPlus / PaperVision / AccuFund / PERCH', 'Existing District systems; existing licensing remains the District\'s responsibility.'],
            ['Separate recurring vendor third-party charges to the District', 'US$ 0. No separate recurring vendor-controlled third-party licence or hosted AI API charge is billed under the baseline implementation.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Section 9.3' }],
      },
    ],
  },

  {
    id: 'INV-02',
    chapter: 'investment',
    route: '/investment/milestones',
    title: 'Milestone Payment Structure',
    headline: 'Payment follows major delivery checkpoints.',
    supporting:
      'Payments attach to formal milestone completion and acceptance, not to development tasks or hours worked.',
    visual: 'payment-rail',
    whyItMatters:
      'Ten percent of the implementation price is deliberately retained across production readiness, go-live and hypercare — so our final payment depends on the system being stable in production, not on it having been delivered.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Section 9.2' },
    ],
    presenterNotes: [
      'Walk the rail once. The shape is the argument: the heaviest payments sit against the three business-domain increments the District can actually see working.',
      'M7, M8 and M9 together are 10%. That is the retention that aligns us with production stabilization.',
      'Milestone invoices become eligible only after acceptance against the criteria in Section 5.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Time and Cost Proposal — time-focused tasks and total not-to-exceed costs'],
    evalTags: ['EVAL-TC'],
    exploreDetails: [
      {
        title: 'Milestone payment schedule',
        table: {
          head: ['Milestone', 'Payment %', 'Payment'],
          rows: [
            ['M1 — Implementation Baseline and Formal Kickoff', '10%', 'US$ 61,000'],
            ['M2 — Core Platform Baseline', '15%', 'US$ 91,500'],
            ['M3 — Land Acquisition Increment', '20%', 'US$ 122,000'],
            ['M4 — Land Management Increment', '20%', 'US$ 122,000'],
            ['M5 — Land Disposition Increment', '10%', 'US$ 61,000'],
            ['M6 — Migration and System Consolidation', '15%', 'US$ 91,500'],
            ['M7 — UAT and Production Readiness', '2%', 'US$ 12,200'],
            ['M8 — Production Go-Live', '3%', 'US$ 18,300'],
            ['M9 — Hypercare Completion and Final Implementation Acceptance', '5%', 'US$ 30,500'],
            ['Total', '100%', 'US$ 610,000'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Section 9.2' }],
      },
    ],
  },

  {
    id: 'INV-03',
    chapter: 'investment',
    route: '/investment/deliverables',
    title: 'What the Investment Delivers',
    headline: 'Investment tied to a complete operational transition.',
    supporting:
      'Not a licence and an implementation service, but a full transition from the current fragmented landscape to an operating system of record, with the District\'s staff trained on it.',
    visual: 'deliverable-categories',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Sections 5.4, 9.1' },
      { kind: 'proposal', label: 'Submitted Form A — Proposal Response' },
    ],
    presenterNotes: [
      'Short scene. It exists to make the price legible against what the District receives.',
      'Note that the 12-month support is a separate commercial line, so the District can evaluate and renew it independently.',
    ],
    estimatedSeconds: 45,
    evalTags: ['EVAL-TC', 'EVAL-OUTCOME'],
  },

  {
    id: 'INV-04',
    chapter: 'investment',
    route: '/investment/accountability',
    title: 'Accountability for Delivery',
    headline: 'We proposed the financial consequences of missing.',
    supporting:
      'The RFP asked respondents to define recommended financial consequences for not meeting deadlines. These are the terms we put forward against ourselves.',
    visual: 'accountability',
    callouts: [
      { label: 'Late milestone', detail: '1% service credit per additional week, capped at 5% of that milestone payment' },
      { label: 'Failed items within tolerance', detail: '1% credit per failed item, capped at 10% of the release payment' },
      { label: 'Failed release', detail: '5% penalty on total implementation price, plus a corrective recovery plan' },
      { label: 'Two consecutive failures', detail: 'Project subject to termination for vendor performance failure' },
    ],
    whyItMatters:
      'A not-to-exceed price protects the District from cost overrun. These terms protect it from the more common failure — a project that stays in budget by shipping something that does not work.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Implementation & Project Management — Methodology; Time and Cost Proposal' },
      { kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Section 9.4' },
    ],
    presenterNotes: [
      'The District asked for this explicitly in two places: the methodology requirement asks respondents to define recommended financial consequences for not meeting deadlines, and Time and Cost is scored on it.',
      'Most proposals answer this vaguely. Answering it concretely is a differentiator — say the numbers.',
      'Be fair about the boundary: consequences apply to delays attributable to Merlion-controlled activities, not to approved scope changes, District-caused delays or third-party dependencies. That symmetry is what makes the commitment credible rather than theatrical.',
      'Close the investment section here and move to technical support.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: [
      'Implementation & Project Management — Methodology, recommended financial consequences',
      'Time and Cost Proposal — financial consequences including failure to meet deadlines',
    ],
    evalTags: ['EVAL-TC', 'EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'Milestone deadline performance',
        body: [
          'Merlion actively manages the approved milestone schedule and provides a documented recovery plan when a vendor-controlled issue threatens an agreed milestone date.',
          'If a formal milestone is delivered more than five business days after its agreed target date solely because of vendor-controlled delay, Merlion provides a service credit equal to 1% of the affected milestone payment for each additional full business week of delay, capped at 5% of that milestone payment.',
          'Financial consequences apply only to delays attributable to vendor-controlled activities. They do not apply to approved scope changes, District-caused delays, unavailable District resources or systems, third-party dependencies, or other conditions outside Merlion\'s reasonable control.',
          'Where the same event also qualifies for a release-performance penalty, consequences are not stacked; the greater applicable consequence applies.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Section 9.4' }],
      },
      {
        title: 'Release acceptance tolerance',
        table: {
          head: ['Failed release items', 'Financial consequence', 'Release status'],
          rows: [
            ['0%', 'No deduction', 'Accepted'],
            ['1%-10%', '1% deduction per failed item, capped at 10% of the applicable release payment', 'Accepted subject to remediation'],
            ['More than 10%', 'Release payment not accepted as complete, and a project-level penalty of 5% of the total implementation price applies', 'Failed release'],
          ],
        },
        body: [
          'If more than 10% of committed release items fail acceptance, the release does not receive formal acceptance until deficiencies are corrected and successfully retested, and Merlion prepares and executes a corrective recovery plan addressing the causes.',
          'If two consecutive releases fail acceptance, the project is subject to termination for vendor performance failure.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Commercial Proposal', locator: 'Section 9.4' }],
      },
    ],
  },

  {
    id: 'INV-05',
    chapter: 'investment',
    route: '/investment/support',
    title: 'Technical Support Investment',
    headline: 'US$40,000 for twelve months of technical support.',
    supporting:
      'Operational support is commercially separate from the implementation price: a 12-month agreement covering Level 1 and Level 2 support, the SLA and the service catalog, renewable by mutual agreement.',
    visual: 'support-investment',
    whyItMatters:
      'Keeping support separate lets the District evaluate, budget and renew it on its own terms. The price covers operating and maintaining the accepted LAMS scope; new functionality is never hidden inside it.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Support Model', locator: 'Sections 10.9, 10.10' },
      { kind: 'proposal', label: 'Submitted Form A — Proposal Response' },
    ],
    presenterNotes: [
      'State the number once: US$40,000 for the first twelve months, independent of and in addition to the US$610,000 implementation price — US$650,000 in total, as on Form A.',
      'Walk what is included: service desk and ticketing, corrective maintenance, application and integration support, monitoring, security and dependency maintenance for vendor-managed components, and SLA reporting.',
      'Be equally clear about what is not: new functional development goes through change requests, and extended or after-hours coverage is optional and separately priced.',
      'Then close — this is the last scene before the final proposition.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 5.E — Technical Support', 'Time and Cost Proposal — total not-to-exceed costs'],
    evalTags: ['EVAL-TC', 'EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'Operational support pricing',
        table: {
          head: ['Service / cost item', 'Commercial basis', 'Price'],
          rows: [
            ['LAMS Operational Support and Maintenance', '12-month agreement; M-F, 8:00 a.m.-5:00 p.m. ET; Level 1 + Level 2 support; SLA and service catalog', 'US$ 40,000 / year'],
            ['Service desk / ticketing platform', 'Included in annual support', 'Included'],
            ['Corrective maintenance', 'Included in annual support', 'Included'],
            ['Application and integration support', 'Included in annual support', 'Included'],
            ['Application monitoring', 'Included for vendor-managed application services', 'Included'],
            ['Security / dependency maintenance', 'Included for vendor-managed LAMS components', 'Included'],
            ['SLA reporting', 'Included in annual support', 'Included'],
            ['New functional development', 'Change request / separately authorized professional services', 'Not included'],
            ['Extended / after-hours coverage', 'Optional service, if requested by the District', 'Separately priced'],
          ],
        },
        body: [
          'The support price is independent from and in addition to the US$610,000 not-to-exceed implementation price. Platform Development plus the initial twelve months of Technical Support total US$650,000, consistent with Official Form A. Renewal, optional extended coverage and separately authorized enhancement services are subject to mutual agreement.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Section 10.10' }],
      },
      {
        title: 'Support boundaries',
        bullets: [
          'Operational support covers operation and maintenance of the accepted LAMS scope: incident management, corrective maintenance, application and integration troubleshooting, reasonable security and dependency maintenance, corrective releases and user assistance.',
          'It does not include material new functionality, new modules or integrations, substantial workflow changes or major data-conversion projects, which are handled by change request.',
          'For incidents caused solely by third-party products or District-managed infrastructure, support includes LAMS-side diagnosis, evidence collection and coordination; correction remains with the applicable owner or provider.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Section 10.9' }],
      },
    ],
  },
]
